import * as React from 'react';
import { useEffect, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { Button, Spinner } from '@/components/Elements';
import { CouponForm } from '@/components/Elements/CouponForm';
import { tinyModalStyles, wideNoScrollModalStyles } from '@/components/Elements/Modal/customStyles';
import { PATH_UPDATE_PAYMENT_METHOD } from '@/config';
import { REQUEST_FAILED, REQUEST_SUCCESS } from '@/config/appConstants';
import { updatePayment } from '@/features/payment';
import { getAllAxiosData, GetterAxios } from '@/lib/axios';
import {
  Currency,
  IntervalBillingMode,
  InvoicePreviewTypes,
  ProductTypes,
  UserProductTypes,
} from '@/types';
import {
  getBillingModeByIntervalCount,
  getCurrencyFromUserProduct,
  getBillingModeFromUserProduct,
  getPricesIdsByCurrencyAndInterval,
  getPromotionCodeByCurrencyAndInterval,
  setterErrorPaymentModal,
} from '@/utils/helpers';

import { ErrorModal } from '../ErrorModal';
import { InvoiceUpdateSummary } from '../InvoiceUpdateSummary';
import { PaymentsModal } from '../PaymentsModal';
import { PendingPaymentErrorModal } from '../PendingPaymentErrorModal';
import { UnpaidMeteredErrorModal } from '../UnpaidMeteredErrorModal';
import { UpdateProductCard } from '../UpdateProductCard/UpdateProductCard';

export type ConfirmNeededPaymentModalProps = {
  isOpen?: boolean;
  onRequestClose: () => void;
  onActionButton: (promotionCode: string | '') => void;
  oldUserProduct?: UserProductTypes;
  newUserProduct: ProductTypes;
  newCurrency: Currency;
  newIntervalCount: IntervalBillingMode;
  isCouponEnabled?: boolean;
  onDemoCouponFormSubmit?: (values: any) => Promise<any>;
  isDemo?: boolean;
  actionButtonLabel?: string;
  initialInvoiceUpdateSummaryData?: InvoicePreviewTypes;
  onSubmitCoupon?: (
    currency: Currency,
    product: ProductTypes | null,
    promotionCode: string | ''
  ) => Promise<void>;
  onConfirmUnpaidMeteredErrorAction: () => void;
};

export const ConfirmNeededPaymentModal = ({
  onRequestClose,
  onActionButton,
  isOpen = false,
  oldUserProduct,
  newUserProduct,
  newCurrency,
  newIntervalCount,
  isCouponEnabled,
  onDemoCouponFormSubmit,
  onSubmitCoupon,
  initialInvoiceUpdateSummaryData,
  actionButtonLabel,
  isDemo = false,
  onConfirmUnpaidMeteredErrorAction,
}: ConfirmNeededPaymentModalProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const getInitialCoupon = () => {
    if (initialInvoiceUpdateSummaryData) {
      return initialInvoiceUpdateSummaryData?.discounts?.[0]?.coupon?.id || '';
    }

    return '';
  };

  const getInitialCouponStatus = () => {
    return getInitialCoupon() ? REQUEST_SUCCESS : undefined;
  };

  const [invoiceUpdateSummaryData, setInvoiceUpdateSummaryData] = useState<any | null>(null);
  const [couponStatus, setCouponStatus] = useState<any | null>(getInitialCouponStatus());
  const [isInvoiceUpdateSummaryDataLoading, setIsInvoiceUpdateSummaryDataLoading] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState<string>(getInitialCoupon());
  const [lastValidCoupon, setLastValidCoupon] = useState<string | undefined>(undefined);
  const [showSpinner, setShowSpinner] = useState(true);
  const [statusPaymentError, setStatusPaymentError] = useState<number>(0);
  const [statusUnpaidMeteredError, setStatusUnpaidMeteredError] = useState(false);
  const [statusPendingPaymentError, setStatusPendingPaymentError] = useState(false);

  useEffect(() => {
    if (initialInvoiceUpdateSummaryData) {
      setInvoiceUpdateSummaryData(initialInvoiceUpdateSummaryData);
      setShowSpinner(false);
      return;
    }

    const getters: GetterAxios[] = [
      {
        callback: updatePayment,
        parameters: {
          preview: true,
          prices: getPricesIdsByCurrencyAndInterval(
            newUserProduct.prices,
            newCurrency,
            newIntervalCount
          ),
          onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => {
            setterErrorPaymentModal(
              setStatusPaymentError,
              setStatusUnpaidMeteredError,
              setStatusPendingPaymentError,
              status,
              detail
            );
          },
          promotionCode: getPromotionCodeByCurrencyAndInterval(
            newUserProduct.prices,
            newCurrency,
            newIntervalCount
          ),
        },
      },
    ];
    const setters: any = [setInvoiceUpdateSummaryData];
    const pageData = getAllAxiosData(getters, setters);

    pageData
      .then(() => {
        setShowSpinner(false);
      })
      .catch((error) => {
        handleError(error);
      });
  }, [
    handleError,
    newUserProduct.prices,
    newCurrency,
    newIntervalCount,
    initialInvoiceUpdateSummaryData,
  ]);

  const onDefaultActionButton = () => {
    onDefaultRequestClose();
    onActionButton && onActionButton(currentCoupon);
  };

  const onDefaultRequestClose = () => {
    onRequestClose && onRequestClose();
  };

  const renderSpinner = () => (
    <PaymentsModal
      closeButton={false}
      isOpen={showSpinner}
      style={tinyModalStyles}
      shouldCloseOnOverlayClick={false}
    >
      <div className="flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    </PaymentsModal>
  );

  const renderStatusUnpaidMeteredErrorModal = () => (
    <UnpaidMeteredErrorModal
      isOpen={statusUnpaidMeteredError}
      onConfirmAction={onDefaultConfirmUnpaidMeteredErrorAction}
      onCancelAction={onDefaultRequestClose}
    />
  );

  const renderStatusPendingPaymentErrorModal = () => (
    <PendingPaymentErrorModal
      isOpen={statusPendingPaymentError}
      onConfirmAction={onDefaultConfirmPendingPaymentErrorAction}
      onCancelAction={onDefaultRequestClose}
    />
  );

  const renderStatusPaymentErrorModal = () => (
    <ErrorModal status={statusPaymentError} onRequestClose={onDefaultRequestClose} />
  );

  const onDefaultCouponFormSubmit = async (values: any) => {
    setIsInvoiceUpdateSummaryDataLoading(true);
    setCurrentCoupon(values.customCoupon);

    let promise;

    if (isDemo && onDemoCouponFormSubmit) {
      promise = onDemoCouponFormSubmit(values);
    } else if (onSubmitCoupon) {
      promise = onSubmitCoupon(newCurrency, newUserProduct, values.customCoupon);
    } else {
      promise = updatePayment({
        preview: true,
        prices: getPricesIdsByCurrencyAndInterval(
          newUserProduct.prices,
          newCurrency,
          newIntervalCount
        ),
        promotionCode: values.customCoupon,
        onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => {
          setterErrorPaymentModal(
            setStatusPaymentError,
            setStatusUnpaidMeteredError,
            setStatusPendingPaymentError,
            status,
            detail
          ) && setCouponStatus(REQUEST_FAILED);
        },
      });
    }

    return promise
      .then((response) => {
        setIsInvoiceUpdateSummaryDataLoading(false);

        if (response) {
          setCouponStatus(REQUEST_SUCCESS);
          setInvoiceUpdateSummaryData(response);
          setLastValidCoupon(values.customCoupon);
        }
      })
      .catch(() => {
        setCouponStatus(REQUEST_FAILED);
        setIsInvoiceUpdateSummaryDataLoading(false);
      });
  };

  const getInvoiceUpdateSummaryStyle = () => {
    if (isCouponEnabled) {
      if (couponStatus === REQUEST_SUCCESS) {
        return 'summary__update--success';
      } else if (couponStatus === REQUEST_FAILED) {
        return 'summary__update--error';
      }
    }

    return;
  };

  const onDefaultConfirmUnpaidMeteredErrorAction = async () => {
    onConfirmUnpaidMeteredErrorAction && (await onConfirmUnpaidMeteredErrorAction());

    onDefaultRequestClose && onDefaultRequestClose();
  };

  const onDefaultConfirmPendingPaymentErrorAction = () => {
    window.location.assign(PATH_UPDATE_PAYMENT_METHOD);
    onDefaultRequestClose && onDefaultRequestClose();
  };

  return (
    <div>
      {!invoiceUpdateSummaryData && renderSpinner()}
      {statusPaymentError > 0 && renderStatusPaymentErrorModal()}
      {statusUnpaidMeteredError && renderStatusUnpaidMeteredErrorModal()}
      {statusPendingPaymentError && renderStatusPendingPaymentErrorModal()}

      {!showSpinner &&
        !statusPaymentError &&
        !statusUnpaidMeteredError &&
        !statusPendingPaymentError && (
          <PaymentsModal
            isOpen={isOpen}
            style={wideNoScrollModalStyles}
            onRequestClose={onDefaultRequestClose}
          >
            <div className="summary__payment__modal modal__content__wrapper max-h-[82vh] overflow-auto">
              {oldUserProduct && (
                <>
                  <h2 className="md__payments__modal__title--h2">
                    {t('ConfirmNeededPaymentModal.title')}
                  </h2>

                  <div className="relative w-full flex gap-1.5 align-center">
                    <div className="md__payments__modal__banner w-1/2">
                      <UpdateProductCard
                        className=""
                        userProduct={oldUserProduct}
                        currency={getCurrencyFromUserProduct(oldUserProduct)}
                        billingMode={getBillingModeFromUserProduct(oldUserProduct)}
                        isANewProductToUpdate={false}
                      />
                    </div>
                    <span className="md__payments__modal__banner--arrow">➝</span>
                    <div className="md__payments__modal__banner w-1/2">
                      <UpdateProductCard
                        className=""
                        product={newUserProduct}
                        currency={newCurrency}
                        billingMode={getBillingModeByIntervalCount(newIntervalCount)}
                        isANewProductToUpdate={true}
                      />
                    </div>
                  </div>
                </>
              )}
              {invoiceUpdateSummaryData && (
                <div className="md__payments__modal__summary">
                  <InvoiceUpdateSummary
                    invoiceUpdateSummaryData={invoiceUpdateSummaryData}
                    isLoading={isInvoiceUpdateSummaryDataLoading}
                    className={getInvoiceUpdateSummaryStyle()}
                  />
                </div>
              )}

              {isCouponEnabled && (
                <div className="md__payments__modal__coupon">
                  <CouponForm
                    className="md__payments__modal__coupon"
                    couponStatus={couponStatus}
                    onSubmit={onDefaultCouponFormSubmit}
                    currentCoupon={currentCoupon}
                    lastValidCoupon={lastValidCoupon}
                    isLoading={isInvoiceUpdateSummaryDataLoading}
                  />
                </div>
              )}
              <div className="md__payments__modal__footer">
                <Button
                  className="m-auto mt-12 py-2 lg:py-4 text-xs lg:text-sm w-[300px] rounded-full"
                  variant="primary"
                  size="free"
                  type="button"
                  title={actionButtonLabel || t('ConfirmNeededPaymentModal.actionButton')}
                  onClick={onDefaultActionButton}
                  spinnerPlacement="start"
                  startIcon={<i className="ri-shopping-bag-line" aria-hidden="true" />}
                >
                  {actionButtonLabel || t('ConfirmNeededPaymentModal.actionButton')}
                </Button>
              </div>
              <p className="md__payments__modal__notice">{t('ConfirmNeededPaymentModal.notice')}</p>
            </div>
          </PaymentsModal>
        )}
    </div>
  );
};
